import { DateTime } from "luxon";

import { cache, sectors, syncState } from "@/services/cache";
import { sectorsSyncKey } from "@/types/products/product-keys";

import { goFetch } from "@/utils/network-utils";
import { retryOnceOnServiceUnavailable } from "@/utils/service-unavailable-retry";
import { ServiceUnavailable } from "@tmi/types/api/responses";

import type { ContentfulSectors, SectorContentfulInfo } from "@tmi/types/api/content-models";
import type { ContentfulSector } from "@tmi/types/entities";
import type { Sector, GetSectorsResponse } from "@tmi/types/api/market-data";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";

const logger = createDefaultLogger( LogLevels.DEBUG );

export interface RefreshSectorsOptions {
    /**
     * tmi-p00, main thread only: on a content/sector-catalog 503 (before the server's first cache fill), retry once
     * after its Retry-After, and on a second 503 return the TMI sectors with their placeholder Contentful fields. The
     * sync worker must leave this off: loadSectorMaps would write those placeholders over the real rows, prune the
     * real ids and lock the cache for 4 h. Without it a 503 rejects at once and the worker tries again next pass.
     */
    placeholdersWhileContentWarms?: boolean;
}

export async function refreshSectorsFromServer( authToken: string | null,
                                                options: RefreshSectorsOptions = {} ): Promise<ContentfulSector[]> {
    const placeholdersWhileContentWarms: boolean = true === options.placeholdersWhileContentWarms;
    let allSectors: ContentfulSector[] = [];

    const t0: number = performance.now();

    // tmi-ck2 C4: `sectors` (was persisted query ec7783c18b91cc181eead26d47c0a14f).
    // The twin takes no arguments and carries no .RequireAuthorization — the token is
    // still passed because a signed-in sweep should be attributable in the service log,
    // not because the route needs it.
    const getTmiSectorsResponse = await goFetch<GetSectorsResponse, never>( {
        serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
        endpoint: 'sentient/sectors',
        verbOverride: 'GET',
        authToken: authToken
    } );

    const tmiSectors = new Map<string, ContentfulSector>();

    if ( null != getTmiSectorsResponse?.data?.sectors?.items ) {
        getTmiSectorsResponse.data.sectors.items.forEach( ( node: Sector ) => {
            tmiSectors.set( node.symbol.toLowerCase(),
                {
                    id: node.id,
                    name: node.name,
                    symbol: node.symbol,
                    // Data that comes from Contentful, but provide default values (useful when the sector hasn't been defined in Contentful yet)
                    slug: node.symbol.toLowerCase(),
                    contentfulId: node.symbol.toLowerCase(),
                } );
        } );

        let cmsSectors: ContentfulSector[] | undefined = [];
        let page: number = 0;
        const pageSize: number = 50;

        while ( true ) {
            const response =
                await retryOnceOnServiceUnavailable(
                    () => goFetch<ContentfulSectors, never>( {
                        serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                        // R26 trap 2: `skip` is a PAGE INDEX on the content catalogs, not a row
                        // offset — the producer asserts it (UngatedCatalogPagingTests). The loop below
                        // already stops on a short page, which is this catalog's only completion
                        // signal: the twin carries no `totalCount` (R26 trap 1).
                        endpoint: `content/sector-catalog?skip=${ page }&limit=${ pageSize }`,
                        verbOverride: 'GET',
                        authToken: authToken
                    } ),
                    { shouldRetry: () => placeholdersWhileContentWarms } )
                    // A second 503 on the main thread: stop paging and keep the placeholders (see the option).
                    .catch( ( err: unknown ) => {
                        if ( placeholdersWhileContentWarms && err instanceof ServiceUnavailable ) return null;
                        throw err;
                    } );

            // If no response or no data, stop pagination
            const currentPageSectors = response?.data?.sectorCatalog?.sectors;
            if ( !currentPageSectors || currentPageSectors.length === 0 ) {
                break;
            }

            // Process the sectors from this page
            cmsSectors = [ ...( response.data.sectorCatalog.sectors
                    .map( ( info: SectorContentfulInfo ): ContentfulSector =>
                        ( {
                            contentfulId: info.id,
                            name: info.name,
                            slug: info.slug.toLowerCase(),
                            symbol: info.short_name,
                            // Data that comes from TMI/II
                            id: 0
                        } ) )
            ) ];

            cmsSectors.forEach( ( contentfulSector: ContentfulSector ) => {
                const tmiSector: ContentfulSector | undefined = tmiSectors.get( contentfulSector.symbol.toLowerCase() );

                if ( tmiSector ) {
                    tmiSector.slug = contentfulSector.slug;
                    tmiSector.contentfulId = contentfulSector.contentfulId;
                }
            } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ currentPageSectors.length } sector items (page ${ page }) took ${ t1 - t0 } ms` );

            // Stop if this page had fewer items than pageSize (last page)
            if ( currentPageSectors.length < pageSize ) {
                break;
            }

            page++;
        }

        allSectors = Array.from( tmiSectors, ( [ _, value ] ) => value );
    }

    return allSectors;
}

export async function loadSectorMaps( sectorByContentfulIdMap: Map<string, ContentfulSector>,
                                      sectorByTmiIdMap: Map<number, ContentfulSector>,
                                      authToken: string | null ): Promise<boolean> {
    let updated = false;
    try {
        const t0: number = performance.now();

        await cache.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.get< number >( sectorsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            const sectorsReceived: ContentfulSector[] = await refreshSectorsFromServer( authToken );

            if ( sectorsReceived.length > 0 ) {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
                await syncState.put( sectorsSyncKey, nextSyncThresholdInMillis );
                updated = true;

                // Purge IDB entries that no longer exist on the server
                const receivedIds = new Set( sectorsReceived.map( s => s.contentfulId ) );
                const keysToRemove = ( await sectors.keys() ).filter( key => !receivedIds.has( key ) );
                await sectors.deleteMany( keysToRemove );

                await sectors.putMany( sectorsReceived );

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ sectorsReceived.length } sectors from server in ${ t1 - t0 } ms (${ keysToRemove.length } stale removed)` );

                sectorByContentfulIdMap.clear();
                sectorByTmiIdMap.clear();
                sectorsReceived.forEach( s => {
                    sectorByContentfulIdMap.set( s.contentfulId, s );
                    sectorByTmiIdMap.set( s.id, s );
                } );
                return updated;
            } else {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.put( sectorsSyncKey, nextSyncThresholdInMillis );
            }
        } else if ( sectorByContentfulIdMap.size > 0 || sectorByTmiIdMap.size > 0 ) {
            return updated;
        }

        // Cold start: populate maps from IDB cache
        sectorByTmiIdMap.clear();
        sectorByContentfulIdMap.clear();
        for ( const sector of await sectors.all() ) {
            sectorByContentfulIdMap.set( sector.contentfulId, sector );
            sectorByTmiIdMap.set( sector.id, sector );
        }

    } catch ( err: any ) {
        logger.error( '[ sector-sync-coordinator ] Sync failed:', err );
    }

    return updated;
}